/**
 * NodeConfigPanel - Right sidebar config panel with n8n-style data mapping.
 * Shows upstream variables from connected nodes and lets users map data between steps.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Node, Edge } from '@xyflow/react';
import {
    McButton,
    McIcon,
    McInput,
    McSelect,
    McTag,
} from '@maersk-global/mds-react-wrapper/components-core';
import { NODE_CONFIGS, AgentNodeType } from './nodes/nodeTypes';
import { AddToolDialog, AVAILABLE_TOOLS } from './AddToolDialog';
import { agentService, PromptTemplateDTO } from '../../services/agentService';
import { MODEL_OPTIONS } from '../../config/modelRegistry';
import {
    AI_PROMPT_SECTION_FIELDS,
    composeSystemPromptFromSections,
    normaliseSections,
} from '../../utils/promptUtils';

/** Maersk mc-input may expose the value on CustomEvent detail as well as the native target. */
function readMcInputValue(e: any): string {
    const d = e?.detail;
    if (d?.value !== undefined && d?.value !== null) return String(d.value);
    const t = e?.target;
    if (typeof t?.value === 'string') return t.value;
    if (t?.value != null) return String(t.value);
    const ne = e?.nativeEvent;
    if (typeof ne?.composedPath === 'function') {
        for (const el of ne.composedPath()) {
            if (
                el &&
                typeof (el as HTMLInputElement).value === 'string' &&
                (el as HTMLElement).tagName === 'INPUT'
            ) {
                return (el as HTMLInputElement).value;
            }
        }
    }
    return '';
}


interface NodeConfigPanelProps {
    node: Node | null;
    nodes: Node[];
    edges: Edge[];
    onUpdate: (nodeId: string, data: Record<string, any>) => void;
    onClose: () => void;
    onDelete: (nodeId: string) => void;
    /** Saves workflow to the server so tool config (e.g. GitHub) is available in Test in Chat / execution. */
    onPersistWorkflow?: () => Promise<void>;
    readOnly?: boolean;
}

const triggerTypes = [
    { label: '📱 WhatsApp', value: 'whatsapp' },
    { label: '💬 Microsoft Teams', value: 'teams' },
    { label: '⏰ Scheduled (Cron)', value: 'cron' },
    { label: '👆 Manual Trigger', value: 'manual' },
];

const modelOptions = MODEL_OPTIONS;

const channelOptions = [
    { label: '📤 Reply on same channel', value: 'same' },
    { label: '📱 WhatsApp', value: 'whatsapp' },
    { label: '💬 Microsoft Teams', value: 'teams' },
    { label: '✉️ Email', value: 'email' },
];

const httpMethods = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
    { label: 'DELETE', value: 'DELETE' },
];

const operatorOptions = [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'not_equals' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Contains', value: 'contains' },
];


/** Output variables each node type produces */
const NODE_OUTPUTS: Record<string, { key: string; description: string }[]> = {
    trigger: [
        { key: 'message', description: 'The incoming user message' },
        { key: 'from', description: "Sender's phone number or ID" },
        { key: 'channel', description: 'Channel type (whatsapp, teams, etc.)' },
    ],
    aiAction: [
        { key: 'output', description: "The AI model's response" },
    ],
    query: [
        { key: 'output', description: 'Query results as text' },
        { key: 'rowCount', description: 'Number of rows returned' },
    ],
    apiCall: [
        { key: 'output', description: 'API response body' },
        { key: 'statusCode', description: 'HTTP status code' },
    ],
    condition: [
        { key: 'result', description: 'true or false' },
    ],
    notify: [],
};

/**
 * Walk edges backwards from a given node to find all upstream nodes
 */
function getUpstreamNodes(nodeId: string, nodes: Node[], edges: Edge[]): Node[] {
    const visited = new Set<string>();
    const result: Node[] = [];
    const queue = [nodeId];

    while (queue.length > 0) {
        const current = queue.shift()!;
        // Find all edges pointing TO this node
        const incomingEdges = edges.filter(e => e.target === current);
        for (const edge of incomingEdges) {
            if (!visited.has(edge.source)) {
                visited.add(edge.source);
                const sourceNode = nodes.find(n => n.id === edge.source);
                if (sourceNode) {
                    result.push(sourceNode);
                    queue.push(sourceNode.id);
                }
            }
        }
    }
    return result;
}

const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({
    node,
    nodes,
    edges,
    onUpdate,
    onClose,
    onDelete,
    onPersistWorkflow,
    readOnly = false,
}) => {
    const [showVariables, setShowVariables] = useState(true);
    const [isAddToolDialogOpen, setIsAddToolDialogOpen] = useState(false);
    const [applySaving, setApplySaving] = useState(false);
    const [applyNotice, setApplyNotice] = useState<{ type: 'ok' | 'error'; message: string } | null>(null);
    const [promptTemplates, setPromptTemplates] = useState<PromptTemplateDTO[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

    // ─── Synchronous ref for tool configs ───────────────────────────────
    // React state updates are async/batched. To guarantee Apply always
    // reads the latest values typed into tool config fields we keep a ref
    // that is updated synchronously on every keystroke.
    const toolConfigsRef = useRef<Record<string, Record<string, any>>>({});
    // A render counter lets us force the display inputs to re-render after
    // each edit so controlled inputs stay in sync with the ref.
    const [, setToolConfigTick] = useState(0);

    const nodeType = (node?.type || 'trigger') as AgentNodeType;
    const config = NODE_CONFIGS[nodeType];
    const data = useMemo((): Record<string, any> => {
        if (!node?.id) return {};
        const fromGraph = nodes.find((n) => n.id === node.id)?.data as Record<string, any> | undefined;
        return (fromGraph ?? (node.data as Record<string, any>) ?? {}) as Record<string, any>;
    }, [node, nodes]);
    const promptTemplateId = typeof data.promptTemplateId === 'string' ? data.promptTemplateId : '';

    // Seed ref from graph data when the selected node changes (and when toolsConfig / legacy fields load).
    useEffect(() => {
        const base = JSON.parse(JSON.stringify(data.toolsConfig || {})) as Record<string, Record<string, unknown>>;
        const tid = typeof data.toolId === 'string' ? data.toolId : '';
        const isToolNode = nodeType === 'tool' && tid;
        if (isToolNode && typeof node?.id === 'string' && base[node.id] && !base[tid]) {
            // Legacy shape stores per-tool config under node id; normalize to tool id key.
            base[tid] = base[node.id];
        }
        const nested = base[tid];
        const hasNestedCreds =
            nested &&
            (String(nested.repository ?? '').trim() !== '' || String(nested.token ?? '').trim() !== '');
        if (isToolNode && !hasNestedCreds && (String(data.repository ?? '').trim() || String(data.token ?? '').trim())) {
            base[tid] = {
                repository: data.repository ?? '',
                token: data.token ?? '',
                branch: data.branch ?? 'main',
            };
        }
        toolConfigsRef.current = base;
        setToolConfigTick((t) => t + 1);
    }, [node?.id, nodeType, data.toolId, data.toolsConfig, data.repository, data.token, data.branch]);

    useEffect(() => {
        if (promptTemplateId) {
            setSelectedTemplateId(promptTemplateId);
        } else {
            // Agent has no linked template — check if sections have content (i.e. custom prompt)
            const currentSections = normaliseSections(data.promptSections);
            const hasAnySectionContent = Object.values(currentSections).some((value) => Boolean(value?.trim()));
            setSelectedTemplateId(hasAnySectionContent ? '__custom__' : '');
        }
    }, [node?.id, promptTemplateId]);

    useEffect(() => {
        setApplyNotice(null);
    }, [node?.id]);

    useEffect(() => {
        if (nodeType !== 'aiAction') {
            setPromptTemplates([]);
            return;
        }

        let mounted = true;

        const fetchPromptTemplates = async () => {
            try {
                const templates = await agentService.getPromptTemplates();
                if (mounted) {
                    setPromptTemplates(Array.isArray(templates) ? templates : []);
                }
            } catch {
                if (mounted) {
                    setPromptTemplates([]);
                }
            }
        };

        fetchPromptTemplates();

        return () => {
            mounted = false;
        };
    }, [nodeType, node?.id]);

    useEffect(() => {
        if (!node || nodeType !== 'aiAction' || promptTemplates.length === 0) {
            return;
        }

        const currentSections = normaliseSections(data.promptSections);
        const hasAnySectionContent = Object.values(currentSections).some((value) => Boolean(value?.trim()));

        if (hasAnySectionContent) {
            return;
        }

        const templateIdToLoad =
            (promptTemplateId && promptTemplates.some((t) => t.templateId === promptTemplateId)
                ? promptTemplateId
                : (promptTemplates.find((t) => t.isDefault)?.templateId || ''));

        if (templateIdToLoad) {
            loadFromTemplate(templateIdToLoad);
        }
    }, [node, nodeType, promptTemplates, promptTemplateId]);

    // Compute upstream variables (must be before early return for hooks rules)
    const upstreamVariables = useMemo(() => {
        if (!node) return [];
        const upstream = getUpstreamNodes(node.id, nodes, edges);
        const vars: { nodeLabel: string; nodeType: string; variable: string; description: string }[] = [];

        for (const upNode of upstream) {
            const upType = upNode.type || 'unknown';
            const upLabel = (upNode.data as any)?.label || upType;
            const outputs = NODE_OUTPUTS[upType] || [];
            for (const output of outputs) {
                vars.push({
                    nodeLabel: upLabel,
                    nodeType: upType,
                    variable: `{{${upType}.${output.key}}}`,
                    description: output.description,
                });
            }
        }
        return vars;
    }, [node?.id, nodes, edges]);

    /** Flyway / legacy tool nodes often set only data.toolId (e.g. github) without enabledTools — still show config UI. */
    const assignedToolIds = useMemo(() => {
        const list = [...(data.enabledTools || [])];
        if (nodeType === 'tool' && typeof data.toolId === 'string' && data.toolId && !list.includes(data.toolId)) {
            list.push(data.toolId);
        }
        return list;
    }, [data.enabledTools, data.toolId, nodeType]);

    if (!node) return null;

    const updateField = (field: string, value: any) => {
        onUpdate(node.id, { ...data, [field]: value });
    };

    const toggleTool = (toolId: string) => {
        const current: string[] = data.enabledTools || [];
        const isCurrentlyEnabled = current.includes(toolId);
        const updated = isCurrentlyEnabled
            ? current.filter((t: string) => t !== toolId)
            : [...current, toolId];

        const newToolsConfig = { ...toolConfigsRef.current };
        if (isCurrentlyEnabled) {
            delete newToolsConfig[toolId];
        }
        toolConfigsRef.current = newToolsConfig;
        setToolConfigTick((t) => t + 1);
        onUpdate(node.id, { ...data, enabledTools: updated, toolsConfig: newToolsConfig });
    };

    /** Synchronously update the ref, bump the render counter, and push to parent. */
    const updateToolConfig = (toolId: string, field: string, value: any) => {
        setApplyNotice(null);
        const prev = toolConfigsRef.current;
        const updated: Record<string, Record<string, any>> = {
            ...prev,
            [toolId]: { ...(prev[toolId] || {}), [field]: value },
        };
        toolConfigsRef.current = updated;
        setToolConfigTick((t) => t + 1);
        onUpdate(node.id, { ...data, toolsConfig: updated });
    };

    /** Read directly from the synchronous ref — never stale. */
    const handleApplyToolConfiguration = async () => {
        if (!node || readOnly) return;
        const configs = toolConfigsRef.current;
        const enabled: string[] = assignedToolIds;

        for (const toolId of enabled) {
            const cfg = configs[toolId] || {};
            const def = AVAILABLE_TOOLS.find((t) => t.id === toolId);
            if (!def?.requiresConfig) continue;

            if (toolId === 'github') {
                const rawRepo = String(cfg.repository ?? '').trim();
                if (!rawRepo) {
                    setApplyNotice({ type: 'error', message: 'GitHub: enter repository (owner/repo).' });
                    return;
                }
                // Auto-normalise full URLs to owner/repo
                const normalised = rawRepo
                    .replace(/[,;.]+$/, '')
                    .replace(/^https?:\/\/github\.com\//i, '')
                    .replace(/\.git$/, '')
                    .replace(/^\/+|\/+$/g, '');
                if (!normalised || !normalised.includes('/')) {
                    setApplyNotice({
                        type: 'error',
                        message: 'GitHub: repository must be in owner/repo format (e.g. Maersk-Global/my-repo).',
                    });
                    return;
                }
                if (normalised !== rawRepo) {
                    updateToolConfig('github', 'repository', normalised);
                }

                if (!String(cfg.token ?? '').trim()) {
                    setApplyNotice({ type: 'error', message: 'GitHub: enter a Personal Access Token.' });
                    return;
                }
                continue;
            }
            for (const f of def.configFields || []) {
                if (!String(cfg[f.key] ?? '').trim()) {
                    setApplyNotice({ type: 'error', message: `${def.label}: ${f.label} is required.` });
                    return;
                }
            }
        }

        // Push the ref state into the graph so executeSave serialises it.
        // Drop legacy top-level GitHub fields (Flyway / old UI) so the backend never reads placeholders
        // instead of toolsConfig.github.
        const mergedEnabled = [
            ...(data.enabledTools || []),
            ...(nodeType === 'tool' && data.toolId ? [String(data.toolId)] : []),
        ];
        const dedupedEnabled = mergedEnabled.filter((id, i) => mergedEnabled.indexOf(id) === i);
        const nextToolsConfig: Record<string, Record<string, any>> = { ...configs };
        if (nodeType === 'tool' && typeof data.toolId === 'string' && typeof node.id === 'string') {
            // Persist canonical key shape (toolsConfig.<toolId>) to avoid refresh/test mismatch.
            if (node.id !== data.toolId && nextToolsConfig[node.id] && !nextToolsConfig[data.toolId]) {
                nextToolsConfig[data.toolId] = nextToolsConfig[node.id];
            }
            delete nextToolsConfig[node.id];
        }
        const nextData: Record<string, unknown> = {
            ...data,
            toolsConfig: nextToolsConfig,
            enabledTools: dedupedEnabled,
        };
        if (assignedToolIds.includes('github')) {
            delete nextData.repository;
            delete nextData.token;
            delete nextData.branch;
        }
        onUpdate(node.id, nextData as typeof data);

        // If tool config is edited on an AI Action node, also copy it to any standalone
        // tool nodes for the same toolId so runtime extraction (which prefers tool nodes)
        // reads the exact values the user just applied.
        if (nodeType === 'aiAction') {
            for (const toolId of dedupedEnabled) {
                const cfg = nextToolsConfig[toolId];
                if (!cfg) continue;

                const matchingToolNodes = nodes.filter((n) => {
                    if (n.type !== 'tool') return false;
                    const nd = (n.data || {}) as Record<string, any>;
                    return String(nd.toolId || '') === toolId;
                });

                for (const toolNode of matchingToolNodes) {
                    const toolNodeData = (toolNode.data || {}) as Record<string, any>;
                    const toolNodeConfigs: Record<string, Record<string, any>> = {
                        ...(toolNodeData.toolsConfig || {}),
                    };

                    toolNodeConfigs[toolId] = {
                        ...(toolNodeConfigs[toolId] || {}),
                        ...cfg,
                    };

                    // Keep canonical key shape: toolsConfig.<toolId>
                    delete toolNodeConfigs[toolNode.id];

                    const toolNodeEnabled = Array.isArray(toolNodeData.enabledTools)
                        ? Array.from(new Set([...toolNodeData.enabledTools, toolId]))
                        : [toolId];

                    onUpdate(toolNode.id, {
                        ...toolNodeData,
                        enabledTools: toolNodeEnabled,
                        toolsConfig: toolNodeConfigs,
                    });
                }
            }
        }

        if (onPersistWorkflow) {
            setApplySaving(true);
            setApplyNotice(null);
            try {
                // onUpdate → handleNodeUpdate eagerly syncs nodesRef inside the
                // setNodes updater, so executeSave always reads the latest graph.
                // No async delay required.
                await onPersistWorkflow();
                setApplyNotice({
                    type: 'ok',
                    message: 'Saved. Runtime and Test in Chat will use this tool configuration.',
                });
            } catch (e: any) {
                setApplyNotice({ type: 'error', message: e?.message || 'Could not save agent.' });
            } finally {
                setApplySaving(false);
            }
        } else {
            setApplyNotice({
                type: 'ok',
                message: 'Node updated locally. Save the agent to persist tool settings.',
            });
        }
    };

    const hasConfigurableTools = assignedToolIds.some((tid: string) => {
        const def = AVAILABLE_TOOLS.find((t) => t.id === tid);
        return Boolean(def?.requiresConfig && def.configFields?.length);
    });

    const getPromptSections = (): Record<string, string> => {
        return normaliseSections(data.promptSections);
    };

    const updatePromptSection = (sectionKey: string, value: string) => {
        const nextSections = { ...getPromptSections(), [sectionKey]: value };
        const composedPrompt = composeSystemPromptFromSections(nextSections);
        onUpdate(node.id, { ...data, promptSections: nextSections, prompt: composedPrompt });
    };

    const loadFromTemplate = (templateId: string) => {
        setSelectedTemplateId(templateId);
        if (!templateId) return;
        const template = promptTemplates.find((t: PromptTemplateDTO) => t.templateId === templateId);
        if (!template) return;
        // Prefer structured contentSections; fall back to putting content into roleContext
        const sections: Record<string, string> = template.contentSections
            && Object.keys(template.contentSections).length > 0
            ? normaliseSections(template.contentSections)
            : normaliseSections({ roleContext: template.content || '' });
        const composedPrompt = composeSystemPromptFromSections(sections);
        onUpdate(node.id, { ...data, promptSections: sections, prompt: composedPrompt, promptTemplateId: templateId });
    };

    /**
     * When the user manually edits a section, detach from the loaded template
     * so the inline systemPrompt takes precedence at runtime.
     */
    const handleSectionEdit = (key: string, value: string) => {
        if (selectedTemplateId && selectedTemplateId !== '__custom__') {
            setSelectedTemplateId('__custom__');
        }
        // Combine section update + template detach in a single onUpdate to avoid race
        const nextSections = { ...getPromptSections(), [key]: value };
        const composedPrompt = composeSystemPromptFromSections(nextSections);
        onUpdate(node.id, { ...data, promptSections: nextSections, prompt: composedPrompt, promptTemplateId: undefined });
    };

    const insertVariable = (variable: string) => {
        navigator.clipboard.writeText(variable);
    };

    return (
        <div className="config-panel">
            {/* Header */}
            <div className="config-panel__header" style={{ borderColor: config.color }}>
                <div className="config-panel__header-top">
                    <span className="config-panel__icon">{config.icon}</span>
                    <McTag fit="small" style={{ '--mc-tag-background': config.bgColor, '--mc-tag-color': config.color } as any}>
                        {config.label}
                    </McTag>
                    <McButton
                        variant="plain"
                        appearance="neutral"
                        onClick={onClose}
                        style={{ marginLeft: 'auto' }}
                    >
                        <McIcon icon="times" />
                    </McButton>
                </div>
                <McInput
                    label="Node name"
                    hiddenlabel
                    value={data.label || ''}
                    onInput={(e: any) => updateField('label', readMcInputValue(e))}
                    placeholder="Node name"
                />
            </div>

            <div className="config-panel__body">
                {/* Description for all nodes */}
                <McInput
                    label="Description"
                    value={data.description || ''}
                    onInput={(e: any) => updateField('description', readMcInputValue(e))}
                    placeholder="What does this step do?"
                />

                {/* ═══════════════ TRIGGER ═══════════════ */}
                {nodeType === 'trigger' && (
                    <>
                        <McSelect
                            label="Trigger Type"
                            value={data.triggerType || 'whatsapp'}
                            data={triggerTypes}
                            optionselected={(e: any) => updateField('triggerType', e.detail.value)}
                        />
                        {(data.triggerType === 'whatsapp' || !data.triggerType) && (
                            <>
                                <McInput
                                    label="WhatsApp Business Number"
                                    value={data.whatsappNumber || ''}
                                    onInput={(e: any) => updateField('whatsappNumber', readMcInputValue(e))}
                                    placeholder="+1 (415) 523-8886"
                                />
                                <div className="config-panel__field">
                                    <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                        Webhook URL
                                    </label>
                                    <div style={{
                                        padding: '8px 12px',
                                        background: '#F3F4F6',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontFamily: 'monospace',
                                        color: '#6B7280',
                                        wordBreak: 'break-all',
                                    }}>
                                        {`${window.location.origin}/apmt-cdh-agent-service/channels/whatsapp/webhook`}
                                    </div>
                                    <span className="mds-text--x-small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                                        Configure this URL in your Twilio WhatsApp sandbox or production settings
                                    </span>
                                </div>
                            </>
                        )}
                        {data.triggerType === 'teams' && (
                            <>
                                <McInput
                                    label="Teams Bot App ID"
                                    value={data.teamsBotAppId || ''}
                                    onInput={(e: any) => updateField('teamsBotAppId', readMcInputValue(e))}
                                    placeholder="Azure AD Bot App ID"
                                />
                            </>
                        )}
                        {data.triggerType === 'cron' && (
                            <McInput
                                label="Cron Expression"
                                value={data.cronExpression || ''}
                                onInput={(e: any) => updateField('cronExpression', readMcInputValue(e))}
                                placeholder="*/15 * * * *"
                            />
                        )}

                        {/* Trigger outputs info */}
                        <div className="config-panel__outputs-info">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                📤 Output Variables
                            </label>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                These variables are available in downstream nodes:
                            </div>
                            {NODE_OUTPUTS.trigger.map(v => (
                                <div key={v.key} className="variable-chip" onClick={() => insertVariable(`{{trigger.${v.key}}}`)}>
                                    <code>{`{{trigger.${v.key}}}`}</code>
                                    <span>{v.description}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* ═══════════════ QUERY ═══════════════ */}
                {nodeType === 'query' && (
                    <>
                        <McInput
                            label="Query ID"
                            value={data.queryId || ''}
                            onInput={(e: any) => updateField('queryId', readMcInputValue(e))}
                            placeholder="Enter Sentinel query UUID or {{variable}}"
                        />
                        <McInput
                            label="Query Name"
                            value={data.queryName || ''}
                            onInput={(e: any) => updateField('queryName', readMcInputValue(e))}
                            placeholder="e.g. Check Appointment Slots"
                        />
                        <McInput
                            label="Terminal ID"
                            value={data.terminalId || ''}
                            onInput={(e: any) => updateField('terminalId', readMcInputValue(e))}
                            placeholder="e.g. ESALG or {{aiAction.terminalId}}"
                        />
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Query Parameters (JSON)
                            </label>
                            <textarea
                                className="config-panel__textarea"
                                rows={3}
                                value={data.parameters || '{}'}
                                onChange={(e) => updateField('parameters', e.target.value)}
                                placeholder='{ "startDate": "{{trigger.message}}" }'
                            />
                            <span className="mds-text--x-small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                                Use {'{{variable}}'} to map data from upstream nodes
                            </span>
                        </div>
                        <McInput
                            label="Max Results"
                            value={String(data.maxResults || 100)}
                            onInput={(e: any) => updateField('maxResults', parseInt(readMcInputValue(e), 10) || 100)}
                            placeholder="100"
                        />
                    </>
                )}

                {/* ═══════════════ AI ACTION ═══════════════ */}
                {nodeType === 'aiAction' && (
                    <>
                        {/* Load from existing prompt template */}
                        {promptTemplates.length > 0 && (
                            <McSelect
                                label="Load from Prompt Template"
                                value={selectedTemplateId}
                                data={[
                                    { label: '— Select a template —', value: '' },
                                    { label: 'Custom (use sections below)', value: '__custom__' },
                                    ...promptTemplates.map((t: PromptTemplateDTO) => ({ label: `${t.name}${t.databaseType ? ` (${t.databaseType})` : ''}`, value: t.templateId })),
                                ]}
                                optionselected={(e: any) => {
                                    const val = e.detail.value;
                                    if (val === '__custom__') {
                                        setSelectedTemplateId('__custom__');
                                        // Detach from any template — the user's own sections are the prompt source
                                        onUpdate(node.id, { ...data, promptTemplateId: undefined });
                                        return;
                                    }
                                    loadFromTemplate(val);
                                }}
                            />
                        )}
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Context Configuration
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {AI_PROMPT_SECTION_FIELDS.map((field) => (
                                    <div key={field.key}>
                                        <label className="mds-text--small-bold" style={{ display: 'block', marginBottom: '6px', color: '#374151' }}>
                                            {field.label}
                                        </label>
                                        <textarea
                                            className="config-panel__textarea"
                                            rows={3}
                                            value={getPromptSections()[field.key] || ''}
                                            onChange={(e) => handleSectionEdit(field.key, e.target.value)}
                                            placeholder={field.placeholder}
                                        />
                                    </div>
                                ))}
                            </div>
                            <span className="mds-text--x-small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                                Stored as structured JSON in `promptSections` and automatically composed into `prompt` for runtime compatibility.
                            </span>
                        </div>
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Model
                            </label>
                            <div style={{
                                display: 'inline-flex',
                                borderRadius: 20,
                                border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                                overflow: 'hidden',
                                marginTop: 4,
                            }}>
                                {(() => {
                                    const savedModel = data.model || 'gpt-4o-mini';
                                    const effectiveModel = modelOptions.some((o: { label: string; value: string }) => o.value === savedModel)
                                        ? savedModel
                                        : modelOptions[0]?.value || 'gpt-4o-mini';
                                    // Auto-migrate: if saved model is not in current options, update it
                                    if (effectiveModel !== savedModel && node) {
                                        setTimeout(() => updateField('model', effectiveModel), 0);
                                    }
                                    return modelOptions.map((opt: { label: string; value: string }) => {
                                        const isActive = effectiveModel === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                onClick={() => updateField('model', opt.value)}
                                                style={{
                                                    padding: '6px 14px',
                                                    fontSize: 12,
                                                    fontWeight: isActive ? 700 : 500,
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    backgroundColor: isActive
                                                        ? 'var(--mds-color-brand-moss-100, #00754a)'
                                                        : 'transparent',
                                                    color: isActive
                                                        ? 'var(--mds-color-neutral-white, #fff)'
                                                        : 'var(--mds-color-neutral-strong-text, #1f2937)',
                                                    transition: 'all 0.15s ease',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Temperature: {data.temperature ?? 0.3}
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={data.temperature ?? 0.3}
                                onChange={(e) => updateField('temperature', parseFloat(e.target.value))}
                                style={{ accentColor: '#7C3AED', width: '100%' }}
                            />
                        </div>
                    </>
                )}

                {/* ── Tools Section (Shared) ── */}
                {(nodeType === 'aiAction' || nodeType === 'tool') && (
                    <div className="config-panel__field">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                    🛠️ Assigned Tools
                                </label>
                                <McButton fit="small" variant="outlined" onClick={() => setIsAddToolDialogOpen(true)}>
                                    <McIcon icon="plus" slot="prefix" /> Add Tool
                                </McButton>
                            </div>

                            <div className="tools-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {assignedToolIds.length === 0 ? (
                                    <div style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                        border: '1px dashed #D1D5DB',
                                        borderRadius: '8px',
                                        color: '#6B7280',
                                        fontSize: '14px'
                                    }}>
                                        No tools assigned. AI will not be able to take actions.
                                    </div>
                                ) : (
                                    assignedToolIds.map((toolId: string) => {
                                        const toolDef = AVAILABLE_TOOLS.find(t => t.id === toolId);
                                        if (!toolDef) return null;

                                        const toolConfig = toolConfigsRef.current[toolId] || {};

                                        return (
                                            <div key={toolId} style={{
                                                border: '1px solid #E5E7EB',
                                                borderRadius: '8px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px',
                                                    backgroundColor: '#F9FAFB',
                                                    borderBottom: toolDef.requiresConfig ? '1px solid #E5E7EB' : 'none'
                                                }}>
                                                    <McIcon icon={toolDef.icon} style={{ marginRight: '12px', color: '#6B7280' }} />
                                                    <div style={{ flexGrow: 1 }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#111827' }}>{toolDef.label}</div>
                                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{toolDef.description}</div>
                                                    </div>
                                                    {!(nodeType === 'tool' && data.toolId === toolId) && (
                                                        <McButton variant="plain" appearance="error" fit="small" onClick={() => toggleTool(toolId)}>
                                                            <McIcon icon="trash" />
                                                        </McButton>
                                                    )}
                                                </div>

                                                {/* Dynamic tool configuration from configFields — only on tool nodes */}
                                                {nodeType === 'tool' && toolDef.requiresConfig && toolDef.configFields && toolDef.configFields.length > 0 && (
                                                    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: 'white' }}>
                                                        {toolDef.configFields.map((field) => (
                                                            <div key={field.key}>
                                                                {field.type === 'select' && field.options ? (
                                                                    <McSelect
                                                                        label={field.label}
                                                                        value={toolConfig[field.key] || field.defaultValue || ''}
                                                                        data={field.options}
                                                                        optionselected={(e: any) =>
                                                                            updateToolConfig(
                                                                                toolId,
                                                                                field.key,
                                                                                e.detail?.value ?? e.detail
                                                                            )
                                                                        }
                                                                    />
                                                                ) : field.type === 'password' ? (
                                                                    <div className="config-panel__field">
                                                                        <label
                                                                            className="mds-text--small-bold"
                                                                            style={{
                                                                                display: 'block',
                                                                                marginBottom: '6px',
                                                                                color: '#374151',
                                                                            }}
                                                                        >
                                                                            {field.label}
                                                                        </label>
                                                                        {/* Native input: mc-input + shadow DOM often omits token from React onInput handlers. */}
                                                                        <input
                                                                            type="password"
                                                                            autoComplete="off"
                                                                            value={toolConfig[field.key] || ''}
                                                                            onChange={(e) =>
                                                                                updateToolConfig(toolId, field.key, e.target.value)
                                                                            }
                                                                            placeholder={field.placeholder || ''}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '8px 12px',
                                                                                border: '1px solid #E5E7EB',
                                                                                borderRadius: '8px',
                                                                                fontSize: '14px',
                                                                                boxSizing: 'border-box',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <McInput
                                                                        label={field.label}
                                                                        value={toolConfig[field.key] || ''}
                                                                        onInput={(e: any) =>
                                                                            updateToolConfig(toolId, field.key, readMcInputValue(e))
                                                                        }
                                                                        placeholder={field.placeholder || ''}
                                                                    />
                                                                )}
                                                                {field.helpText && (
                                                                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px', fontStyle: 'italic' }}>
                                                                        {field.helpText}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {nodeType === 'tool' && hasConfigurableTools && !readOnly && (
                                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <McButton
                                        appearance="primary"
                                        fit="small"
                                        disabled={applySaving}
                                        onClick={() => void handleApplyToolConfiguration()}
                                    >
                                        <McIcon icon="check" slot="prefix" />
                                        {applySaving ? 'Saving…' : 'Apply tool configuration'}
                                    </McButton>
                                    <span className="mds-text--x-small-normal" style={{ color: '#6B7280' }}>
                                        Validates required fields and saves the agent so integrations (e.g. GitHub read/list tools)
                                        receive repository, branch, and token when you run or test the agent.
                                    </span>
                                    {applyNotice && (
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                padding: '8px 10px',
                                                borderRadius: '8px',
                                                backgroundColor: applyNotice.type === 'ok' ? '#ECFDF5' : '#FEF2F2',
                                                color: applyNotice.type === 'ok' ? '#065F46' : '#991B1B',
                                            }}
                                        >
                                            {applyNotice.message}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                )}

                {/* ═══════════════ CONDITION ═══════════════ */}
                {nodeType === 'condition' && (
                    <>
                        <McInput
                            label="Field"
                            value={data.field || ''}
                            onInput={(e: any) => updateField('field', readMcInputValue(e))}
                            placeholder="e.g. {{aiAction.output}}, {{query.rowCount}}"
                        />
                        <McSelect
                            label="Operator"
                            value={data.operator || 'equals'}
                            data={operatorOptions}
                            optionselected={(e: any) => updateField('operator', e.detail.value)}
                        />
                        <McInput
                            label="Value"
                            value={data.value || ''}
                            onInput={(e: any) => updateField('value', readMcInputValue(e))}
                            placeholder="e.g. book_appointment, 85"
                        />
                    </>
                )}



                {/* ═══════════════ API CALL ═══════════════ */}
                {nodeType === 'apiCall' && (
                    <>
                        <McSelect
                            label="HTTP Method"
                            value={data.method || 'POST'}
                            data={httpMethods}
                            optionselected={(e: any) => updateField('method', e.detail.value)}
                        />
                        <McInput
                            label="URL"
                            value={data.url || ''}
                            onInput={(e: any) => updateField('url', readMcInputValue(e))}
                            placeholder="https://api.example.com/appointments"
                        />
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Request Body (JSON)
                            </label>
                            <textarea
                                className="config-panel__textarea"
                                rows={4}
                                value={data.body || ''}
                                onChange={(e) => updateField('body', e.target.value)}
                                placeholder='{ "containerId": "{{containerId}}" }'
                            />
                            <span className="mds-text--x-small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                                Use {'{{variable}}'} to reference data from previous steps
                            </span>
                        </div>
                    </>
                )}

                {/* ═══════════════ NOTIFY ═══════════════ */}
                {nodeType === 'notify' && (
                    <>
                        <McSelect
                            label="Response Channel"
                            value={data.channel || 'same'}
                            data={channelOptions}
                            optionselected={(e: any) => updateField('channel', e.detail.value)}
                        />
                        <div className="config-panel__field">
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151' }}>
                                Message Template
                            </label>
                            <textarea
                                className="config-panel__textarea"
                                rows={4}
                                value={data.messageTemplate || ''}
                                onChange={(e) => updateField('messageTemplate', e.target.value)}
                                placeholder="Results from {{query.output}}: {{aiAction.output}}"
                            />
                            <span className="mds-text--x-small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                                Use {'{{variable}}'} to insert dynamic values from upstream nodes
                            </span>
                        </div>
                    </>
                )}

                {/* ═══════════════ AVAILABLE VARIABLES (all non-trigger nodes) ═══════════════ */}
                {nodeType !== 'trigger' && upstreamVariables.length > 0 && (
                    <div className="config-panel__variables-section">
                        <div
                            className="config-panel__variables-header"
                            onClick={() => setShowVariables(!showVariables)}
                        >
                            <label className="mds-text--small-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: '#374151', cursor: 'pointer' }}>
                                {showVariables ? '▾' : '▸'} 📥 Available Input Variables
                            </label>
                            <span className="mds-text--x-small-normal" style={{ color: '#9CA3AF' }}>
                                Click to copy
                            </span>
                        </div>
                        {showVariables && (
                            <div className="variables-list">
                                {upstreamVariables.map((v, i) => (
                                    <div
                                        key={i}
                                        className="variable-chip"
                                        onClick={() => insertVariable(v.variable)}
                                        title={`Click to copy ${v.variable}`}
                                    >
                                        <code>{v.variable}</code>
                                        <span>{v.description}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="config-panel__footer">
                <McButton
                    variant="outlined"
                    appearance="error"
                    onClick={() => onDelete(node.id)}
                    style={{ width: '100%' }}
                >
                    <McIcon icon="trash" slot="prefix" />
                    Delete Node
                </McButton>
            </div>

            <AddToolDialog
                open={isAddToolDialogOpen}
                onClose={() => setIsAddToolDialogOpen(false)}
                onAddTools={(toolIds) => {
                    const current: string[] = [...assignedToolIds];
                    const newTools = toolIds.filter((id) => !current.includes(id));
                    if (newTools.length > 0) {
                        const toolsConfig = { ...toolConfigsRef.current };
                        for (const id of newTools) {
                            if (!toolsConfig[id]) toolsConfig[id] = {};
                        }
                        toolConfigsRef.current = toolsConfig;
                        setToolConfigTick((t) => t + 1);
                        onUpdate(node.id, { ...data, enabledTools: [...current, ...newTools], toolsConfig });
                    }
                    setIsAddToolDialogOpen(false);
                }}
                existingTools={assignedToolIds}
            />
        </div>
    );
};

export default NodeConfigPanel;
